import { api } from "@/lib/api-client"

// Local AI Lead Types
// NOTE: Document field names (`ambassadorOid`, `ambassadorName`, `ambassadorEmail`)
// keep their legacy prefix because they match the stored Cosmos document schema.
// Only API paths and TS symbols were rebranded to "Local AI Lead".
export type LocalAiLeadMapping = {
  id: string
  ambassadorOid: string
  ambassadorName: string
  ambassadorEmail: string
  assignedUsers: Array<{ oid: string; name: string; email: string }>
  assignedGroups?: string[]
  assignedGroupsAttribute?: string | null
  createdAt: string
  updatedAt: string
  createdBy: string
}

export async function getLocalAiLeads(): Promise<{ localAiLeads: LocalAiLeadMapping[] }> {
  const response = await api.get("/admin/local-ai-leads")
  return response.data || response
}

export async function upsertLocalAiLead(
  oid: string,
  body: {
    ambassadorName: string
    ambassadorEmail: string
    assignedUsers: Array<{ oid: string; name: string; email: string }>
    assignedGroups?: string[]
  }
): Promise<LocalAiLeadMapping> {
  const response = await api.put(`/admin/local-ai-leads/${oid}`, body)
  return response.data || response
}

export async function deleteLocalAiLead(oid: string): Promise<{ success: boolean }> {
  const response = await api.delete(`/admin/local-ai-leads/${oid}`)
  return response.data || response
}

// Local AI Lead feature configuration

export type LocalAiLeadGroupAttribute = "department" | "companyName" | "officeLocation" | "city"

export type FixedCostCurrency = "USD" | "SEK" | "EUR"

export type LocalAiLeadDashboardSection =
  | "requests_per_user"
  | "shared_cost_lines"
  | "user_full_cost"
  | "headcount_adoption"
  | "adoption_curve"
  | "group_overview"

export type LocalAiLeadDashboardSections = Record<LocalAiLeadDashboardSection, boolean>

export const LOCAL_AI_LEAD_DASHBOARD_SECTIONS: LocalAiLeadDashboardSection[] = [
  "requests_per_user",
  "shared_cost_lines",
  "user_full_cost",
  "headcount_adoption",
  "adoption_curve",
  "group_overview",
]

export function emptyDashboardSections(): LocalAiLeadDashboardSections {
  return {
    requests_per_user: false,
    shared_cost_lines: false,
    user_full_cost: false,
    headcount_adoption: false,
    adoption_curve: false,
    group_overview: false,
  }
}

export type LocalAiLeadHeadcount = {
  employees: number
  updatedBy?: string | null
  updatedAt?: string | null
}

export type LocalAiLeadConfig = {
  group_attribute: LocalAiLeadGroupAttribute
  /** Azure AD group OID to scope directory searches to platform users. Null = no filter. */
  user_group_id?: string | null
  /** Display name of the scoping group. */
  user_group_name?: string | null
  /** Combined fixed monthly cost (platform infrastructure + subscription), cost tile. */
  monthly_platform_cost?: number
  /** @deprecated Legacy split-out subscription cost; folded into monthly_platform_cost. Read-only. */
  monthly_subscription_cost?: number
  /** Flat cost added once per assigned user (NOT divided across global active users), cost tile. */
  fixed_infra_cost_per_user?: number
  /** Currency the fixed cost figures above are entered in. */
  fixed_cost_currency?: FixedCostCurrency
  /** Annual project work, divided like the monthly platform cost. Zero changes nothing. */
  annual_project_cost?: number
  /** Annual per-seat benchmark for the adoption curve. Not part of the cost. */
  seat_benchmark_annual?: number
  /** Optional blocks on the Local AI Lead dashboard. Missing switches are off. */
  dashboard_sections?: Partial<LocalAiLeadDashboardSections>
  headcounts?: Record<string, LocalAiLeadHeadcount>
  cost_model?: "legacy" | "shared_annual_v1"
  reporting_currency?: FixedCostCurrency
  cost_model_enabled_from?: string
  assigned_visits_enabled?: boolean
  assigned_visits_started_at?: string | null
}

export type LocalAiLeadConfigResponse = {
  local_ai_lead_config: LocalAiLeadConfig
  allowed_group_attributes: LocalAiLeadGroupAttribute[]
}

export async function getLocalAiLeadConfig(): Promise<LocalAiLeadConfigResponse> {
  const response = await api.get("/admin/local-ai-lead-config")
  return response.data || response
}

export async function updateLocalAiLeadConfig(
  config: Partial<LocalAiLeadConfig>
): Promise<{ local_ai_lead_config: LocalAiLeadConfig }> {
  const response = await api.put("/admin/local-ai-lead-config", config)
  return response.data || response
}

export type LocalAiLeadRefreshResultItem = {
  leadOid: string
  leadName: string
  groupValue?: string
  addedCount: number
  skipped?: boolean
  skipReason?: string
}

export type LocalAiLeadRefreshResponse = {
  leadsRefreshed: number
  leadsSkipped: number
  totalAddedUsers: number
  groupAttribute: LocalAiLeadGroupAttribute
  results: LocalAiLeadRefreshResultItem[]
}

/**
 * Additively sync every Local AI Lead's assignedUsers with Entra by their
 * configured group attribute (e.g. department). Existing assignments are
 * never removed — manual overrides are preserved.
 */
export type LocalAiLeadGroupOverviewRow = {
  groupValue: string | null
  employees: number | null
  activeUsers: number
  adoption: number | null
  requestsPerUser: number | null
  usageMonthly: number
  sharedMonthly: number
  infraMonthly: number
  totalMonthly: number
  perUserMonthly: number | null
  tokenSharePct?: number | null
  requestsPerUserLastMonth?: number | null
  requestsPerUser3m?: number | null
  requestTrend?: "up" | "down" | "flat" | null
  headcountUpdatedAt?: string | null
  headcountUpdatedBy?: string | null
}

export type LocalAiLeadSteeringSummary = {
  globalActiveUsers: number
  sharedPoolMonthly: number
  usageMonthly: number
  totalMonthly: number
  annualTotalEstimate: number
  annualPerActiveUser: number | null
  benchmarkAnnualTotal: number | null
  benchmarkDifference: number | null
  historyMonthsAvailable: number
  reportedEmployees: number
  reportingCompanies: number
  outstandingCompanies: number
  groupEmployees: number | null
  groupEmployeesSource: "manual" | "reported" | "unknown"
  groupAdoption: number | null
  groupHeadcountUpdatedAt: string | null
  groupHeadcountUpdatedBy: string | null
}

export type LocalAiLeadGroupOverviewResponse = {
  enabled: boolean
  currency: string
  costMonth?: string
  rangeDays?: number
  groupAttribute?: LocalAiLeadGroupAttribute
  groups: LocalAiLeadGroupOverviewRow[]
  summary?: LocalAiLeadSteeringSummary
}

export async function updateLocalAiLeadHeadcountOverride(body: {
  employees: number | null
  expectedUpdatedAt: string | null
}): Promise<{
  employees: number | null
  updatedBy: string | null
  updatedAt: string | null
}> {
  const response = await api.put("/admin/local-ai-lead-headcount-override", body)
  return response.data || response
}

export async function getLocalAiLeadGroupOverview(
  currency: FixedCostCurrency
): Promise<LocalAiLeadGroupOverviewResponse> {
  const response = await api.get(
    `/admin/local-ai-lead-group-overview?currency=${encodeURIComponent(currency)}`
  )
  return response.data || response
}

export type LocalAiLeadAnnualCostVersion = {
  id: string
  revision: number
  effectiveFrom: string
  recordedAt: string
  recordedBy: string
  currency: FixedCostCurrency
  annualSubscription: number
  annualCloudInfrastructure: number
  annualProjectWork: number
  annualBenchmark: number
}

export type LocalAiLeadFxVersion = {
  id: string
  revision: number
  yearMonth: string
  USDToReportingCurrency: number
  recordedAt: string
  recordedBy: string
}

export async function getLocalAiLeadCostLedger(yearMonth: string): Promise<{
  costVersions: LocalAiLeadAnnualCostVersion[]
  fxVersions: LocalAiLeadFxVersion[]
}> {
  const response = await api.get(
    `/admin/local-ai-lead-cost-ledger?year_month=${encodeURIComponent(yearMonth)}`
  )
  return response.data || response
}

export async function appendLocalAiLeadAnnualCost(body: {
  expectedRevision: number
  effectiveFrom: string
  currency: FixedCostCurrency
  annualSubscription: number
  annualCloudInfrastructure: number
  annualProjectWork: number
  annualBenchmark: number
}): Promise<LocalAiLeadAnnualCostVersion> {
  const response = await api.post("/admin/local-ai-lead-cost-ledger/cost", body)
  return response.data || response
}

export async function appendLocalAiLeadFx(body: {
  expectedRevision: number
  yearMonth: string
  USDToReportingCurrency: number
}): Promise<LocalAiLeadFxVersion> {
  const response = await api.post("/admin/local-ai-lead-cost-ledger/fx", body)
  return response.data || response
}

export async function enableLocalAiLeadAnnualCost(): Promise<{
  costModel: "shared_annual_v1"
  reportingCurrency: FixedCostCurrency
  enabledFrom: string
}> {
  const response = await api.post("/admin/local-ai-lead-cost-ledger/enable", {})
  return response.data || response
}

export async function refreshLocalAiLeadAssignments(): Promise<LocalAiLeadRefreshResponse> {
  const response = await api.post("/admin/local-ai-leads/refresh")
  return response.data || response
}
